import React, { PropsWithChildren, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useLocation,
  Redirect,
  useRouteMatch,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import WalletSelect from "./pages/walletSelect";
import CreateWallet from "./pages/createWallet";
import ImportWallet from "./pages/importWallet";
import VerifyWallet from "./pages/verifyWallet";
import LockWallet from "./pages/lockWallet";
import UnlockWallet from "./pages/unlockWallet";
import HomePage from "./pages/home";
import Studio from "./pages/studio";
import ViewWalletPage from "./pages/viewWallet";
import WalletDetailsPage from "./pages/walletDetails";
import { WalletStore } from "./store/WalletStore";
import NetworkSwitch from "./containers/NetworkSwitch";
import { checkQueryForDrop } from "./lib/drop/createDrop";
import ExplorerAccountPage from "./pages/explorerAccount";
import ExplorerAssetPage from "./pages/explorerAsset";
import { getActiveNetwork } from "./lib/network";
import { NetworkType } from "./lib/network/types";

export enum Routes {
  NEW = "/new",
  CREATE = "/create",
  VERIFY = "/verify",
  IMPORT = "/import",
  WALLET = "/wallet",
  ACCOUNT = "/account",
  LOCK = "/lock",
  UNLOCK = "/unlock",
  STUDIO = "/studio",
}

const SHOW_NAV_OPTION_ROUTES = [
  Routes.WALLET.toString(),
  Routes.ACCOUNT.toString(),
  Routes.STUDIO.toString(),
  "/account/:address",
  "/asset/:id",
];


interface CustomWindow extends Window {
  analytics?: any;
}

const Container = ({ children }: PropsWithChildren<any>) => {
  const wallet = WalletStore.useState((s) => s.wallet);
  const query = useQuery();
  checkQueryForDrop(query);
  const location = useLocation();
  const match = useRouteMatch();
  const [activeNetwork, setActiveNetwork] = useState(
    () => getActiveNetwork().network
  );

  const handleSwitch = (network: NetworkType) => {
    setTimeout(() => {
      if (wallet) {
        wallet.updateBalance();
        wallet.updateAssetBalances();
      }
      setActiveNetwork(network);
    }, 1000);
  };
  return (
    <div className="">
      {activeNetwork === "testnet" && (
        <div className="w-full bg-yellow-400 flex justify-center text-black py-1 font-medium">
          You are on the testnet
        </div>
      )}
      {/* <div
        className=""
        style={{
          width: "200vw",
          height: "200vh",
          transform: "translate(-50vw, -100vh)",
          backgroundImage:
            "radial-gradient(0% 0% at 0% 0%, #433F79, rgb(0,0,0) 100%",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: -1,
        }}
      ></div> */}
      <header className="container mx-auto px-8">
        <div className="w-full mt-6 flex flex-col ">
          <div className="w-full flex items-center">
            <Link to="/">
              <TokodotLogo />
            </Link>
            <div className="ml-6 transform scale-75 md:scale-100 flex items-end">
              <NetworkSwitch onSwitch={handleSwitch} />
            </div>
          </div>
          {SHOW_NAV_OPTION_ROUTES.includes(match.path) && (
            <div className="w-full flex items-center justify-center text-lg font-semibold text-white mt-6">
              {wallet && (
                <>
                  <div
                    className={`ml-12 border-b-2  hover:border-tokodot-primary ${
                      location.pathname === Routes.WALLET
                        ? "border-tokodot-primary"
                        : "border-transparent"
                    }`}
                  >
                    <Link to={Routes.WALLET}>Wallet</Link>
                  </div>
                  <div
                    className={`ml-12 border-b-2 hover:border-tokodot-primary ${
                      location.pathname === Routes.STUDIO
                        ? "border-tokodot-primary"
                        : "border-transparent"
                    }`}
                  >
                    <Link to={Routes.STUDIO}>Studio</Link>
                  </div>
                  <div
                    className={`ml-12 border-b-2  hover:border-tokodot-primary ${
                      location.pathname === Routes.ACCOUNT
                        ? "border-tokodot-primary"
                        : "border-transparent"
                    }`}
                  >
                    <Link to={Routes.ACCOUNT}>Account</Link>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>
      {children}
    </div>
  );
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}


function AnalyticsContainer() {
  const location = useLocation();
  const [prevPath, setPrevPath] = useState("");

  const customWindow: CustomWindow = window;

  // listen to location and create event
  useEffect(() => {
    if (location.pathname !== prevPath) {
      if (customWindow.analytics) {
        customWindow.analytics.page();
      }
      setPrevPath(location.pathname);
    }
  }, [location]);


  return <></>;
}

export default function App() {
    return (
    <Router>
      <AnalyticsContainer />
      <div>
        <ToastContainer />
        <Switch>
          <Route exact path={Routes.NEW}>
            <Container>
              <WalletSelect />
            </Container>
          </Route>
          <Route path={Routes.WALLET}>
            <Container>
              <ViewWalletPage />
            </Container>
          </Route>
          <Route exact path="/">
            <HomePage />
          </Route>
          <Route exact path={Routes.CREATE}>
            <Container>
              <CreateWallet />
            </Container>
          </Route>
          <Route exact path={Routes.IMPORT}>
            <Container>
              <ImportWallet />
            </Container>
          </Route>
          <Route exact path={Routes.VERIFY}>
            <Container>
              <VerifyWallet />
            </Container>
          </Route>
          <Route exact path={Routes.ACCOUNT}>
            <Container>
              <WalletDetailsPage />
            </Container>
          </Route>
          <Route exact path={Routes.LOCK}>
            <Container>
              <LockWallet />
            </Container>
          </Route>
          <Route exact path={Routes.UNLOCK}>
            <Container>
              <UnlockWallet />
            </Container>
          </Route>
          <Route exact path={Routes.STUDIO}>
            <Container>
              <Studio />
            </Container>
          </Route>
          <Route path="/account/:address">
            <Container>
              <ExplorerAccountPage />
            </Container>
          </Route>
          <Route path="/asset/:id">
            <Container>
              <ExplorerAssetPage />
            </Container>
          </Route>
          <Redirect to={Routes.WALLET} />
        </Switch>
      </div>
    </Router>
  );
}

const TokodotLogo = () => (
  // <svg
  //   width="256"
  //   height="55"
  //   viewBox="0 0 315 55"
  //   fill="none"
  //   xmlns="http://www.w3.org/2000/svg"
  // >
  //   <path
  //     d="M110.525 21.7273H105.393V15.1932H97.8295V21.7273H94.1009V27.4091H97.8295V41.6136C97.794 46.9581 101.434 49.6037 106.92 49.3729C108.874 49.3018 110.259 48.9112 111.022 48.6626L109.832 43.0341C109.46 43.1051 108.661 43.2827 107.95 43.2827C106.441 43.2827 105.393 42.7145 105.393 40.6193V27.4091H110.525V21.7273ZM127.482 49.5327C135.756 49.5327 140.905 43.8686 140.905 35.4702C140.905 27.0185 135.756 21.3722 127.482 21.3722C119.207 21.3722 114.058 27.0185 114.058 35.4702C114.058 43.8686 119.207 49.5327 127.482 49.5327ZM127.517 43.6733C123.7 43.6733 121.746 40.1754 121.746 35.4169C121.746 30.6584 123.7 27.1428 127.517 27.1428C131.263 27.1428 133.217 30.6584 133.217 35.4169C133.217 40.1754 131.263 43.6733 127.517 43.6733ZM187.247 49.5327C195.521 49.5327 200.67 43.8686 200.67 35.4702C200.67 27.0185 195.521 21.3722 187.247 21.3722C178.973 21.3722 173.824 27.0185 173.824 35.4702C173.824 43.8686 178.973 49.5327 187.247 49.5327ZM187.283 43.6733C183.465 43.6733 181.512 40.1754 181.512 35.4169C181.512 30.6584 183.465 27.1428 187.283 27.1428C191.029 27.1428 192.982 30.6584 192.982 35.4169C192.982 40.1754 191.029 43.6733 187.283 43.6733ZM215.638 49.4439C220.042 49.4439 222.332 46.9048 223.38 44.6321H223.7V49H231.157V12.6364H223.611V26.3082H223.38C222.368 24.0888 220.184 21.3722 215.621 21.3722C209.637 21.3722 204.577 26.0241 204.577 35.3991C204.577 44.5256 209.424 49.4439 215.638 49.4439ZM218.036 43.4247C214.325 43.4247 212.3 40.1222 212.3 35.3636C212.3 30.6406 214.289 27.3913 218.036 27.3913C221.711 27.3913 223.771 30.4986 223.771 35.3636C223.771 40.2287 221.675 43.4247 218.036 43.4247ZM249.698 49.5327C257.972 49.5327 263.122 43.8686 263.122 35.4702C263.122 27.0185 257.972 21.3722 249.698 21.3722C241.424 21.3722 236.275 27.0185 236.275 35.4702C236.275 43.8686 241.424 49.5327 249.698 49.5327ZM249.734 43.6733C245.916 43.6733 243.963 40.1754 243.963 35.4169C243.963 30.6584 245.916 27.1428 249.734 27.1428C253.48 27.1428 255.433 30.6584 255.433 35.4169C255.433 40.1754 253.48 43.6733 249.734 43.6733ZM282.546 21.7273H277.415V15.1932H269.851V21.7273H266.122V27.4091H269.851V41.6136C269.816 46.9581 273.455 49.6037 278.942 49.3729C280.895 49.3018 282.28 48.9112 283.044 48.6626L281.854 43.0341C281.481 43.1051 280.682 43.2827 279.972 43.2827C278.463 43.2827 277.415 42.7145 277.415 40.6193V27.4091H282.546V21.7273Z"
  //     fill="white"
  //   />
  //   <path
  //     d="M145.823 49H153.387V40.3352L155.429 38.0092L162.869 49H171.729L161.093 33.5348L171.214 21.7273H162.531L153.795 32.0788H153.387V12.6364H145.823V49Z"
  //     fill="#9A93FF"
  //   />
  //   <circle cx="33.7542" cy="41.2459" r="6.63559" fill="#8F79FF" />
  //   <path
  //     d="M24.7051 13C25.4487 19.4757 22.1026 38.1848 5 42.4915M43.2949 13C42.5513 19.4757 45.8974 38.1848 63 42.4915"
  //     stroke="white"
  //     strokeWidth="8"
  //     strokeLinecap="square"
  //   />
  //   <line
  //     x1="8.93213"
  //     y1="10.4736"
  //     x2="59.0677"
  //     y2="10.4736"
  //     stroke="white"
  //     strokeWidth="8"
  //   />
  // </svg>
  <svg
    width="179"
    height="41"
    viewBox="0 0 179 41"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M62.5149 14.6364H59.4361V10.7159H54.8977V14.6364H52.6605V18.0455H54.8977V26.5682C54.8764 29.7749 57.0604 31.3622 60.3523 31.2237C61.5241 31.1811 62.3551 30.9467 62.8132 30.7976L62.0994 27.4205C61.8757 27.4631 61.3963 27.5696 60.9702 27.5696C60.0646 27.5696 59.4361 27.2287 59.4361 25.9716V18.0455H62.5149V14.6364ZM72.6889 31.3196C77.6534 31.3196 80.7429 27.9212 80.7429 22.8821C80.7429 17.8111 77.6534 14.4233 72.6889 14.4233C67.7244 14.4233 64.6349 17.8111 64.6349 22.8821C64.6349 27.9212 67.7244 31.3196 72.6889 31.3196ZM72.7102 27.804C70.4197 27.804 69.2479 25.7053 69.2479 22.8501C69.2479 19.995 70.4197 17.8857 72.7102 17.8857C74.9581 17.8857 76.13 19.995 76.13 22.8501C76.13 25.7053 74.9581 27.804 72.7102 27.804ZM108.548 31.3196C113.513 31.3196 116.602 27.9212 116.602 22.8821C116.602 17.8111 113.513 14.4233 108.548 14.4233C103.584 14.4233 100.494 17.8111 100.494 22.8821C100.494 27.9212 103.584 31.3196 108.548 31.3196ZM108.57 27.804C106.279 27.804 105.107 25.7053 105.107 22.8501C105.107 19.995 106.279 17.8857 108.57 17.8857C110.817 17.8857 111.989 19.995 111.989 22.8501C111.989 25.7053 110.817 27.804 108.57 27.804ZM125.583 31.2663C128.225 31.2663 129.599 29.7429 130.228 28.3793H130.42V31H134.894V9.18182H130.366V17.3849H130.228C129.621 16.0533 128.31 14.4233 125.572 14.4233C121.982 14.4233 118.946 17.2145 118.946 22.8395C118.946 28.3153 121.854 31.2663 125.583 31.2663ZM127.021 27.6548C124.795 27.6548 123.58 25.6733 123.58 22.8182C123.58 19.9844 124.773 18.0348 127.021 18.0348C129.227 18.0348 130.462 19.8991 130.462 22.8182C130.462 25.7372 129.205 27.6548 127.021 27.6548ZM146.019 31.3196C150.983 31.3196 154.073 27.9212 154.073 22.8821C154.073 17.8111 150.983 14.4233 146.019 14.4233C141.055 14.4233 137.965 17.8111 137.965 22.8821C137.965 27.9212 141.055 31.3196 146.019 31.3196ZM146.04 27.804C143.75 27.804 142.578 25.7053 142.578 22.8501C142.578 19.995 143.75 17.8857 146.04 17.8857C148.288 17.8857 149.46 19.995 149.46 22.8501C149.46 25.7053 148.288 27.804 146.04 27.804ZM165.728 14.6364H162.649V10.7159H158.111V14.6364H155.873V18.0455H158.111V26.5682C158.089 29.7749 160.273 31.3622 163.565 31.2237C164.737 31.1811 165.568 30.9467 166.026 30.7976L165.312 27.4205C165.089 27.4631 164.609 27.5696 164.183 27.5696C163.278 27.5696 162.649 27.2287 162.649 25.9716V18.0455H165.728V14.6364Z"
      fill="white"
    />
    <path
      d="M83.6939 31H88.2322V25.8011L89.4574 24.4055L93.9212 31H99.2372L92.8558 21.7209L98.9283 14.6364H93.7188L88.4773 20.8473H88.2322V9.18182H83.6939V31Z"
      fill="#7167FF"
    />
    <path
      d="M35 27.6629V12.9962C34.9993 12.3532 34.8296 11.7217 34.5078 11.165C34.186 10.6084 33.7235 10.1461 33.1667 9.82457L20.3333 2.49124C19.7759 2.16942 19.1436 2 18.5 2C17.8564 2 17.2241 2.16942 16.6667 2.49124L3.83333 9.82457C3.27648 10.1461 2.81396 10.6084 2.49218 11.165C2.1704 11.7217 2.00066 12.3532 2 12.9962V27.6629C2.00066 28.3059 2.1704 28.9374 2.49218 29.4941C2.81396 30.0508 3.27648 30.5131 3.83333 30.8346L16.6667 38.1679C17.2241 38.4897 17.8564 38.6591 18.5 38.6591C19.1436 38.6591 19.7759 38.4897 20.3333 38.1679L33.1667 30.8346C33.7235 30.5131 34.186 30.0508 34.5078 29.4941C34.8296 28.9374 34.9993 28.3059 35 27.6629Z"
      stroke="url(#paint0_linear)"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 28L19.0002 15L35 28"
      stroke="#7167FF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 37L19 16"
      stroke="#7167FF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="18.5"
        y1="2"
        x2="18.5"
        y2="38.6591"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#3C95DA" />
        <stop offset="1" stopColor="#6A54DE" />
      </linearGradient>
    </defs>
  </svg>
);
